import { useState } from 'react';

import { EnvVars } from '@/../environment';
import { useTranslation } from 'react-i18next';

import Button from '@/components/atoms/Button';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import { capitalizeFirstLetter } from '@/helpers/filterNames';

interface RecordingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (details: RecordingDetails) => void;
  isLoading?: boolean;
}

export interface RecordingDetails {
  name: string;
  objective: string;
  deponent: string;
  otherSpeakers: string;
  phraseList: string;
}

export const RecordingDetailsModal = ({ isOpen, onClose, onConfirm, isLoading }: RecordingDetailsModalProps) => {
  const { t } = useTranslation();

  const isLocal = EnvVars.ENVIRONMENT === 'local';
  const [details, setDetails] = useState<RecordingDetails>(
    isLocal
      ? {
          name: 'Sackler',
          objective:
            'To identify whether Dr Richard Sackler was aware of the addictive potential of OxyContin and to what extent he was involved in the marketing strategies that downplayed these risks.',
          deponent: 'Dr Richard Sackler',
          otherSpeakers: 'Deposing Attorney',
          phraseList: '',
        }
      : {
          name: '',
          objective: '',
          deponent: '',
          otherSpeakers: '',
          phraseList: '',
        },
  );

  const handleConfirm = () => {
    onConfirm(details);
  };

  const handleClose = () => {
    // Reset form on close
    setDetails({
      name: '',
      objective: '',
      deponent: '',
      otherSpeakers: '',
      phraseList: '',
    });
    onClose();
  };

  const modalContent = (
    <div className="p-6">
      <h2 className="mb-2 text-xl font-semibold text-gray-900">Recording Details</h2>

      <p className="mb-4 text-xs text-gray-500">
        Currently, this feature is recommended for interviews under 60 minutes. For longer interviews, we suggest
        starting a new recording.
      </p>

      <div className="space-y-4">
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium text-gray-700">
            Recording Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            placeholder="Enter a name for this recording"
            value={details.name}
            onChange={(e) => setDetails({ ...details, name: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="objective" className="mb-1 block text-sm font-medium text-gray-700">
            Objective <span className="text-red-500">*</span>
          </label>
          <textarea
            id="objective"
            placeholder="Describe the objective of this deposition, interview, or hearing..."
            value={details.objective}
            onChange={(e) => setDetails({ ...details, objective: e.target.value })}
            className="min-h-[80px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="deponent" className="mb-1 block text-sm font-medium text-gray-700">
            {capitalizeFirstLetter(t('witness'))}/Interviewee <span className="text-red-500">*</span>
          </label>
          <input
            id="deponent"
            type="text"
            placeholder="The name of the person testifying, or being deposed/interviewed"
            value={details.deponent}
            onChange={(e) => setDetails({ ...details, deponent: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="otherSpeakers" className="mb-1 block text-sm font-medium text-gray-700">
            Other Speakers
          </label>
          <input
            id="otherSpeakers"
            type="text"
            placeholder="List other speakers (comma separated)"
            value={details.otherSpeakers}
            onChange={(e) => setDetails({ ...details, otherSpeakers: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="phraseList" className="mb-1 block text-sm font-medium text-gray-700">
            Key Terms <span className="text-sm font-normal text-gray-500">(for transcription accuracy)</span>
          </label>
          <textarea
            id="phraseList"
            placeholder="Enter key places, people, brand names, technical terms, etc."
            value={details.phraseList}
            onChange={(e) => setDetails({ ...details, phraseList: e.target.value })}
            className="min-h-[80px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button text="Cancel" onClick={handleClose} type="secondary" size="small" disabled={isLoading} rounded="base" />
        <Button
          text="Start Recording"
          onClick={handleConfirm}
          type="primary"
          size="small"
          disabled={isLoading || !details.name.trim() || !details.deponent.trim() || !details.objective.trim()}
          loading={isLoading}
          rounded="base"
        />
      </div>
    </div>
  );

  return <CustomModal content={modalContent} isOpen={isOpen} handleClose={handleClose} maxWidth="32rem" />;
};
